<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import ReinsurersRepository from '@repositories/reinsurers.repository';
import { IReinsurer } from '../../types/reinsurer.type';
import { ITemplateUI } from '../../types/templatesUI.type';
import CompaniesRepository from '@repositories/companies.repository';
import TemplatesUIDataProviderRepository from '@repositories/templatesUI.repository';
import { ICompany } from '../../types/company.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingReinsurers = ref<boolean>(false);

const templatesUIRepository = new TemplatesUIDataProviderRepository();

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const reinsurers = ref<IReinsurer[]>([]);
const companies = ref<ICompany[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedReinsurer = ref<IReinsurer | null>(null);
const templateData = ref<Array<ITemplateUI>>([]);
const isViewActive = ref<boolean>(true);
const searchFormRef = ref<any>(null);

const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: t('global.nombre'),
  },
  {
    key: 'restore',
    label: t('global.restaurar'),
  },
]);

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: t('global.nombre'),
  },
  {
    key: 'template',
    label: 'TemplateUI',
    formatter: (e: any) => e?.name || '-',
  },
  {
    key: 'companies',
    label: t('global.compania'),
    formatter: (e: any) => e?.map((e: any) => e.name).join(',') || null,
  },
  {
    key: 'reinsurerInfo',
    label: t('global.editar'),
  },
  {
    key: 'delete',
    label: t('global.eliminar'),
  },
]);

const fieldsFilter: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.reaseguro.buscar_por_nombre'),
    value: null,
    cols: 3,
  },
]);

// API
const getReinsurersFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingReinsurers.value = true;
  try {
    const { data } = await ReinsurersRepository.getReinsurersProPaginated({
      page,
      deleted,
      ...params,
    });
    if (data && Array.isArray(data?.reinsurers)) {
      paginationData.value = data.pagination;
      updateReinsurers(data.reinsurers);
      return data.reinsurers;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching reinsurers:', error);
    return [];
  } finally {
    isLoadingReinsurers.value = false;
  }
};

const getCompaniesFromApi = async () => {
  try {
    const data = await CompaniesRepository.getCompanies(true);
    if (Array.isArray(data?.companies)) {
      companies.value = data.companies;
    }
  } catch (error) {
    console.error('Error while fetching companies:', error);
  }
};

const getTemplatesUIFromAPI = async () => {
  try {
    const data = await templatesUIRepository.getTemplates();
    if (Array.isArray(data?.data)) {
      templateData.value = data.data;
    }
  } catch (error) {
    console.error('Error while fetching templates:', error);
  }
};

const updateReinsurers = (newReinsurers: IReinsurer[]) => {
  reinsurers.value = newReinsurers;
};

const loadReinsurers = async () => {
  await getReinsurersFromAPI(1);
};

onMounted(() => {
  loadReinsurers();
  getCompaniesFromApi();
  getTemplatesUIFromAPI();
});

// Create inputs from modals
const fieldsInputReinsurers = computed(() => {
  return [
    {
      key: 'name',
      title: t('ajustes.reaseguro.nombre_reasegurador'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.reaseguro.nombre_reasegurador'),
      value: selectedReinsurer.value?.name,
      cols: 12,
    },
    {
      key: 'templateId',
      title: 'Template ID',
      type: 'select',
      label: 'name',
      validations: {
        rules: {
          required: true,
        },
      },
      options: templateData,
      placeholder: 'ID de template',
      value: selectedReinsurer.value?.templateId,
      formatValue: (val: any) => val?.id,
      cols: 12,
    },
    {
      key: 'companies',
      label: 'name',
      selectLabel: null,
      value: selectedReinsurer.value?.companies || [],
      searchable: true,
      title: t('global.compania'),
      trackBy: 'id',
      validations: {
        rules: {
          required: true,
        },
      },
      type: 'select',
      multiple: true,
      placeholder: t('ajustes.reaseguro.selecciona_companias'),
      formatValue: (val: any) => val.map((v: any) => v.id),
      cols: 12,
      options: companies,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getReinsurersFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.reaseguro.agregar_reasegurador');
  isModalOpen.value = true;
  modalType.value = 'modalCreateReinsurer';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedReinsurer.value = null;
};

const handleActive = async () => {
  const reinsurersData = await getReinsurersFromAPI(1);
  updateReinsurers(reinsurersData);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const reinsurersDeleted = await getReinsurersFromAPI(1, true);
  updateReinsurers(reinsurersDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: IReinsurer) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.reaseguro.msg_borrar_reasegurador')} ${item.id}`
    : `${t('ajustes.reaseguro.msg_restaurar_reasegurador')} ${item.id}`;
  modalType.value = 'modalDialog';
  selectedReinsurer.value = item;
  isModalOpen.value = true;
};

// Filter Functions
const handleOnSubmit = async ({ items }: any) => {
  getReinsurersFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getReinsurersFromAPI(1, false);
};

// CRUD
const handleCreateReinsurer = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const dataToSend = {
      ...items,
      companies: items.companies || [],
    };
    const data = await ReinsurersRepository.createReinsurer(dataToSend);
    $toast?.success(t('ajustes.reaseguro.msg_agregar_reasegurador'));
  } catch (error) {
    $toast?.error(t('ajustes.reaseguro.msg_error_creacion'));
  }
  getReinsurersFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleSelectReinsurer = (reinsurerData: IReinsurer) => {
  modalTitle.value = t('ajustes.reaseguro.actualizar_reasegurador');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateReinsurer');
  selectedReinsurer.value = reinsurerData;
};

const handleUpdateReinsurer = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedReinsurer.value) return;
  const reinsurerForUpdate = {
    ...items,
    id: selectedReinsurer.value.id,
    companies: items.companies || [],
  };

  try {
    await ReinsurersRepository.updateReinsurer(reinsurerForUpdate);
    $toast?.success(t('ajustes.reaseguro.msg_actualizado'));
  } catch (error) {
    $toast?.error(t('ajustes.reaseguro.msg_error_actualizacion'));
  }
  getReinsurersFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleDeleteRestoreReinsurer = async () => {
  if (selectedReinsurer?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await ReinsurersRepository.deleteReinsurer(
          selectedReinsurer.value.id,
        );
      } else {
        response = await ReinsurersRepository.restoreReinsurer(
          selectedReinsurer.value.id,
        );
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('ajustes.reaseguro.msg_error_eliminar'));
      }
      $toast?.error(t('ajustes.reaseguro.msg_error_restaurar'));
    }
    getReinsurersFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
    handleClose();
  }
};
</script>

<template>
  <Layout title="Reaseguradores" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>{{ t('ajustes.reaseguro.agregar_reasegurador') }}</template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingReinsurers"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="reinsurers"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(reinsurerInfo)="assetData">
          <div
            @click="handleSelectReinsurer(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateReinsurer'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputReinsurers"
            @onSubmit="handleCreateReinsurer"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.reaseguro.agregar_reasegurador') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateReinsurer'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputReinsurers"
            @onSubmit="handleUpdateReinsurer"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el reasegurador ${selectedReinsurer?.name}?`
                : `¿Está seguro que desea restaurar el reasegurador ${selectedReinsurer?.name}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreReinsurer"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
